import { computed } from 'vue'
import { useRoute } from 'vue-router'
import {
  HomeIcon,
  ChefHatIcon,
  CalendarIcon,
  ShoppingCartIcon,
  UserIcon,
  SparklesIcon,
  MenuIcon,
  SettingsIcon,
} from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth.js'

/**
 * Single source of truth for the app's primary navigation, shared by the
 * mobile bottom bar (BottomNav) and the desktop top nav (AppHeader) so the two
 * renderings never drift.
 *
 * Each tab may declare `match`: path prefixes that should light the tab. Home
 * is exact-match only because "/" is a prefix of every route. The account tab
 * matches the whole account cluster so Settings/Family/Notifications keep the
 * Profile tab active.
 */
export function usePrimaryNav() {
  const route = useRoute()
  const authStore = useAuthStore()

  const settingsLink = { name: 'Settings', to: '/settings', label: 'settings.title', icon: SettingsIcon, match: ['/settings'] }

  const desktopTabs = computed(() => {
    if (authStore.user) {
      return [
        { name: 'Home', to: '/', label: 'nav_home', icon: HomeIcon, match: ['/'] },
        { name: 'Recipes', to: '/recipes', label: 'recipes_nav', icon: ChefHatIcon, match: ['/recipes', '/recipe/'] },
        { name: 'Planner', to: '/planner', label: 'planner_title', icon: CalendarIcon, match: ['/planner'] },
        { name: 'Groceries', to: '/groceries', label: 'groceries', icon: ShoppingCartIcon, match: ['/groceries'] },
        {
          name: 'Profile',
          to: '/profile',
          label: 'nav_profile',
          icon: UserIcon,
          match: ['/profile', '/settings', '/family-group', '/manage-notifications', '/delete-account', '/cookie-preferences', '/upgrade'],
        },
      ]
    }

    return [
      { name: 'Home', to: '/', label: 'nav_home', icon: HomeIcon, match: ['/'] },
      { name: 'Recipes', to: '/recipes', label: 'recipes_nav', icon: ChefHatIcon, match: ['/recipes', '/recipe/'] },
      settingsLink,
      { name: 'GetStarted', to: '/planner', label: 'intro.get_started', icon: SparklesIcon, match: ['/planner', '/ai-diet-planner'] },
    ]
  })

  const tabs = computed(() => [
    { name: 'Home', to: '/', label: 'nav_today', icon: HomeIcon, match: ['/'] },
    { name: 'Planner', to: '/planner', label: 'nav_plan', icon: CalendarIcon, match: ['/planner', '/ai-diet-planner'] },
    { name: 'Groceries', to: '/groceries', label: 'groceries', icon: ShoppingCartIcon, match: ['/groceries'] },
    {
      name: 'More',
      label: 'nav_more',
      icon: MenuIcon,
      action: 'more',
      match: [
        '/recipes',
        '/recipe/',
        '/profile',
        '/settings',
        '/family-group',
        '/manage-notifications',
        '/delete-account',
        '/cookie-preferences',
        '/upgrade',
      ],
    },
  ])

  const moreLinks = computed(() => [
    { name: 'Recipes', to: '/recipes', label: 'recipes_nav', icon: ChefHatIcon, match: ['/recipes', '/recipe/'] },
    ...(authStore.user
      ? [{ name: 'Profile', to: '/profile', label: 'nav_profile', icon: UserIcon, match: ['/profile'] }]
      : []),
    settingsLink,
  ])

  const isActive = (tab) => {
    const prefixes = tab.match ?? [tab.to]
    const path = route.path
    return prefixes.some((prefix) => {
      if (prefix === '/') return path === '/'
      if (prefix.endsWith('/')) return path.startsWith(prefix)
      return path === prefix || path.startsWith(`${prefix}/`)
    })
  }

  return { tabs, desktopTabs, moreLinks, isActive }
}
